/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {memo, MouseEventHandler} from 'react';
import {makeStyles} from '@material-ui/core';
import {EventCodeValueType, getEventName} from '@ion/core/src/services/EventCodes';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsAlt, dlsColor} from '../../theme/theme';
import {getFormattedDate} from '../../utils/ComponentUtils';
import MiniVideoPlayer from '../videoplayer/MiniVideoPlayer';

export type FeaturedEventProps = {
  event: GenericObject;
  eventClick?: MouseEventHandler<HTMLButtonElement>;
  videoSource: string;
  displayEventName?: boolean;
};

const useStyles = makeStyles(theme => {
  return {
    container : {
      minHeight                    : 150,
      padding                      : '0px',
      [theme.breakpoints.up('sm')] : {
        padding   : '15px 15px 10px',
        '&:hover' : {
          backgroundColor : dlsColor('paleBlue')
        }
      }
    },
    featuredEvent : {
      display                        : 'flex',
      flexDirection                  : 'column',
      paddingTop                     : '10px',
      [theme.breakpoints.down('sm')] : {
        padding : '5px 15px 10px'
      }
    },
    eventLabel : {
      ...dlsAlt('typography', 'cta2'),
      color    : dlsColor('aaBlue'),
      fontSize : 12,
      cursor   : 'pointer'
    },
    eventTimeLabel : {
      ...dlsAlt('typography', 'cta2'),
      color      : dlsColor('grey3'),
      fontWeight : 500,
      fontSize   : 10,
      marginTop  : 4
    }
  };
});

/**
 * VisionFeaturedEvent component renders the featured event for the vision accordion.
 *
 * ```typescript
 *
 * import VisionFeaturedEvent from '../../components/vision/VisionFeaturedEvent';
 *
 * <VisionFeaturedEvent event={featuredEvent}
 *  eventClick={openVisionModal(featuredEvent)}
 *  videoSource={videoSource} />
 *
 * ```
 *
 * @props event - event object
 * @props eventClick - event click handler
 * @props videoSource - video source url
 */
const VisionFeaturedEvent = ({
  event,
  eventClick,
  videoSource,
  displayEventName = true
}: FeaturedEventProps) => {
  const classes = useStyles({displayEventName});

  return (
    <div className={classes.container}>
      <MiniVideoPlayer urls={[videoSource]}
        handleOpenModal={eventClick}
        isMiniPlayer
        audio
        isControls />
      {displayEventName && (
        <div className={classes.featuredEvent}>
          <span className={classes.eventLabel} onClick={eventClick}>
            {getEventName({
              from : EventCodeValueType?.eventCode,
              val  : parseInt(event?.avlEventCode)
            })}
          </span>
          <span className={classes.eventTimeLabel}>
            {getFormattedDate(event?.appmsgEventTime)}
          </span>
        </div>
      )}
    </div>
  );
};

export default memo(VisionFeaturedEvent);
